import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  Utensils,
  Bike,
  Receipt,
  ShoppingBag,
  ArrowRight,
  Table2,
  Sparkles,
  X as XIcon,
  Wallet,
  Package,
  AlertTriangle,
  CheckCircle2,
  ListChecks,
} from 'lucide-react';
import { TipPrimerosPasos } from '@renderer/components/TipPrimerosPasos';
import { contarPasosPendientes } from '@renderer/lib/pasosPendientes';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { DashboardMesero } from '@renderer/pages/DashboardMesero';
import { MesasGrid } from '@renderer/components/mesas/MesasGrid';
import { StockCriticoCard } from '@renderer/components/inventario/StockCriticoCard';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourDashboard } from '@renderer/tours';
import { formatPrecio } from '@renderer/utils/formatters';
import { toLocalYYYYMMDD } from '@renderer/utils/dateLocal';
import { AperturaDelDia } from '@renderer/components/apertura/AperturaDelDia';
import type {
  CorteCajaVista,
  MesaVista,
  PedidoVista,
  ResumenDiaVista,
} from '@shared/types';

interface Estado {
  corte: CorteCajaVista | null;
  resumen: ResumenDiaVista | null;
  pedidosAbiertos: PedidoVista[];
  mesas: MesaVista[];
  /** Sprint 31 — conteo de insumos con semáforo amarillo/rojo. */
  insumosCriticos: number;
  /** Sprint 62 — Catálogo vacío = no hay nada que vender todavía. */
  productos: number;
  cargando: boolean;
}

/** Sprint 9.0: refresco automático del Dashboard. */
const REFRESCO_MS = 5_000;

/**
 * Sprint 31 — Dashboard reorganizado. Prioridad de arriba hacia abajo:
 * (1) ¿algo urgente? → zona de atención condicional;
 * (2) ¿qué hago? → 3 CTAs;
 * (3) ¿cómo va el día / qué pasa ahora? → franja de estado + pedidos + salón.
 * Cada dato aparece una sola vez; las cards de "todo bien" no gastan espacio.
 */
export function Dashboard() {
  const navigate = useNavigate();
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);

  const [est, setEst] = useState<Estado>({
    corte: null,
    resumen: null,
    pedidosAbiertos: [],
    mesas: [],
    insumosCriticos: 0,
    productos: 0,
    cargando: true,
  });
  // Sprint 39 — bump para forzar recarga tras abrir caja (sin reload de página).
  const [refreshKey, setRefreshKey] = useState(0);
  /**
   * Sprint 71 — Cuántos pasos de la guía faltan, para el aviso "¿Sabías que…?".
   *
   * `null` mientras se averigua: el aviso no aparece hasta saberlo, porque salir con "te faltan 4"
   * y corregirlo un segundo después a 0 es peor que no decir nada.
   */
  const [pasosPendientes, setPasosPendientes] = useState<number | null>(null);

  useEffect(() => {
    void contarPasosPendientes().then(setPasosPendientes);
  }, [refreshKey]);

  useEffect(() => {
    let alive = true;
    const cargar = async () => {
      const [c, r, p, m, sc, prods] = await Promise.all([
        window.api.corte.abierto(),
        window.api.reportes.ventasDelDia(),
        window.api.pedidos.listarPorEstado([
          'abierto',
          'pendiente_cocina',
          'cocina',
          'listo',
          'en_ruta',
          'entregado',
          'no_entregado',
        ]),
        window.api.mesas.listar(),
        window.api.insumos.stockCritico(),
        window.api.productos.listar(),
      ]);
      if (!alive) return;
      setEst({
        corte: c.ok ? c.data : null,
        resumen: r.ok ? r.data : null,
        pedidosAbiertos: p.ok ? p.data : [],
        mesas: m.ok ? m.data : [],
        insumosCriticos: sc.ok
          ? sc.data.filter((x) => x.semaforo !== 'verde').length
          : 0,
        productos: prods.ok ? prods.data.length : 0,
        cargando: false,
      });
    };
    cargar();
    const t = setInterval(cargar, REFRESCO_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Sprint 9.0: índice mesaId -> pedido activo para badges de la grid.
  // Sprint 39: este hook DEBE ejecutarse en TODOS los renders (regla de hooks
  // de React); por eso lo dejamos antes de cualquier return condicional.
  const pedidoPorMesa = useMemo(() => {
    const mapa = new Map<number, PedidoVista>();
    for (const p of est.pedidosAbiertos) {
      if (p.mesaId) mapa.set(p.mesaId, p);
      for (const id of p.mesaIds ?? []) {
        if (!mapa.has(id)) mapa.set(id, p);
      }
    }
    return mapa;
  }, [est.pedidosAbiertos]);

  if (!sesion) return null;
  if (sesion.rol === 'mesero') return <DashboardMesero />;
  /**
   * Sprint 63 (Fase 5) — Cocina no tiene tablero: tiene una cola.
   *
   * Sin esto, un usuario de cocina que entre a `/dashboard` vería la pantalla del dueño
   * y todas sus consultas serían rechazadas por la matriz — una pantalla de errores en
   * lugar de su trabajo.
   */
  if (sesion.rol === 'cocina') return <Navigate to="/cocina" replace />;

  const esDueno = sesion.rol === 'dueño';
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const cajaAbierta = !!est.corte;

  // Sprint 39 — Si la caja NO está abierta, mostramos la pantalla de apertura
  // del día EN LUGAR del dashboard normal. Bloquea el flujo hasta que la
  // dueña abra la caja explícitamente (regla de negocio: sin caja no hay
  // pedidos — el backend también lo enforce).
  if (esDueno && !est.cargando && !cajaAbierta) {
    /**
     * Sprint 71.1 — El aviso también va aquí.
     *
     * Reporte del negocio: *"el modal de ¿SABÍAS QUE… ni aparece"*. Y era exacto: con la caja
     * cerrada el tablero devuelve la pantalla de apertura y **nunca llegaba** al render donde lo
     * había montado. O sea que el aviso existía para una pantalla que la dueña ve DESPUÉS de abrir
     * la caja, y no para la primera que ve al entrar — que es justo el momento en que necesita
     * saber a dónde ir.
     */
    return (
      <>
        <TipPrimerosPasos pasosPendientes={pasosPendientes} />
        <AperturaDelDia onCajaAbierta={() => setRefreshKey((k) => k + 1)} />
      </>
    );
  }
  const ventasHoy = est.resumen?.totalVentas ?? 0;
  /*
    Sprint 111 — Se retiran `pedidosCount` y `pedidosCocina`. Quedaron huérfanos en el **Sprint
    108**, cuando se quitó la tarjeta "Pedidos del día" por llevar exactamente a donde lleva
    "Pedidos" del menú lateral (ver el comentario más abajo, donde vivía). Se calculaban en cada
    render para nadie, y un cálculo que ya no se lee es una invitación a revivir la tarjeta sin
    saber por qué se retiró.
  */
  const pedidosNoEntregados = est.pedidosAbiertos.filter(
    (p) => p.estado === 'no_entregado',
  ).length;

  const mesasLibres = est.mesas.filter((m) => m.estado === 'disponible').length;
  const mesasOcupadas = est.mesas.length - mesasLibres;

  return (
    <div className="space-y-5">
      {/*
        Sprint 71 — "¿Sabías que…?", el aviso que dice dónde está la guía.
        Pedido del negocio: *"para que sepa a dónde ir mientras se acostumbra"*. Se apaga solo
        cuando ya no falta ningún paso, aunque la casilla siga marcada.
      */}
      <TipPrimerosPasos pasosPendientes={pasosPendientes} />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Hola, {sesion.nombre}</h1>
          <p className="text-slate-500">
            {est.cargando ? 'Cargando estado del día...' : '¿Qué quieres hacer hoy?'}
          </p>
        </div>
        <HelpButton tour={tourDashboard} />
      </div>

      {/*
        Sprint 62 — Catálogo vacío: lo único que tiene sentido ofrecer es armarlo.
        Sin esto, la dueña entra a un tablero con tres botones de vender que llevan a
        un punto de venta sin nada que vender. Reporte de campo: "ahora que el sistema
        está vacío ESTOY PERDIDO, ¿quién me dice el orden en que debo crear las cosas?".
      */}
      {esDueno && !est.cargando && est.productos === 0 && (
        <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800">
            <ListChecks className="h-5 w-5 text-primary" />
            Todavía no hay nada que vender
          </h2>
          <p className="mt-1 text-sm text-slate-700">
            Antes de cobrar hay que capturar el menú, y el orden importa. Te lo voy indicando paso
            por paso, y cada paso se marca solo cuando lo terminas.
          </p>
          <button
            onClick={() => navigate('/primeros-pasos')}
            className="btn-primary mt-3 inline-flex min-h-[44px] items-center gap-2"
          >
            Empezar por aquí <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ========== ZONA DE ATENCIÓN — sólo aparece si algo requiere acción ========== */}
      {esDueno && !est.cargando && (
        <ZonaAtencion
          cajaAbierta={cajaAbierta}
          pedidosNoEntregados={pedidosNoEntregados}
          insumosCriticos={est.insumosCriticos}
        />
      )}

      {/* ========== 3 CTAs directas ========== */}
      <div className="grid gap-4 md:grid-cols-3" data-tour="dsh-ctas">
        <button
          onClick={() => navigate('/pos?tipo=comer_aqui')}
          className="group flex items-center gap-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 p-5 text-left text-white shadow-lg transition hover:shadow-xl hover:scale-[1.01]"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20">
            <Utensils className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide opacity-80">Vender</div>
            <div className="text-xl font-bold">Comer aquí</div>
            <div className="text-xs opacity-90">Cliente en mesa</div>
          </div>
          <ArrowRight className="h-5 w-5 opacity-70 group-hover:translate-x-1 transition" />
        </button>

        <button
          /*
            Sprint 95 — Al tablero, no directo a capturar.

            Este botón se escribió cuando la única forma de hacer un domicilio era teclearla tú
            misma. Ahora hay tres formas de empezar y un tablero que gobierna el flujo: aterrizar
            ahí te dice **qué te está esperando**, y capturar sigue siendo un toque más.
          */
          onClick={() => navigate('/domicilios')}
          className="group flex items-center gap-4 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 p-5 text-left text-white shadow-lg transition hover:shadow-xl hover:scale-[1.01]"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20">
            <Bike className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide opacity-80">Vender</div>
            <div className="text-xl font-bold">A domicilio</div>
            <div className="text-xs opacity-90">Se envía a la casa del cliente</div>
          </div>
          <ArrowRight className="h-5 w-5 opacity-70 group-hover:translate-x-1 transition" />
        </button>

        {/*
          Sprint 108 — La tercera puerta es **vender**, no "operar".

          Aquí vivía "Pedidos del día", que llevaba exactamente a donde lleva "Pedidos" del menú
          lateral: dos botones para lo mismo, y el patrón de la fila roto —*vender · vender ·
          operar*—. Reporte del dueño del producto: *"quita ese card inútil"*.

          En su lugar va la venta de mostrador, que hasta este sprint **no tenía puerta**: el tipo
          existía en el POS pero el Dashboard entra con `?tipo=comer_aqui` predefinido, así que el
          selector de tres tarjetas no llegaba a mostrarse nunca. Ahora el inicio pregunta lo único
          que hay que decidir al empezar: **qué clase de venta es ésta**.

          Los números que daba ese card (cuántos pedidos, cuántos en cocina) siguen abajo, en los
          chips por estado, que es donde se leen sin ocupar una fila entera.
        */}
        <button
          onClick={() => navigate('/mostrador')}
          className="group flex items-center gap-4 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 p-5 text-left text-white shadow-lg transition hover:shadow-xl hover:scale-[1.01]"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20">
            <ShoppingBag className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide opacity-80">Vender</div>
            <div className="text-xl font-bold">Venta de mostrador</div>
            <div className="text-xs opacity-90">El cliente pasa a recogerlo</div>
          </div>
          <ArrowRight className="h-5 w-5 opacity-70 group-hover:translate-x-1 transition" />
        </button>
      </div>

      {/* ========== Sprint 25 — Card proactiva de precarga (condicional) ========== */}
      {esDueno && <PrecargaSugeridaCard />}

      {/* ========== FRANJA DE ESTADO — cómo va el día, de un vistazo ========== */}
      {esDueno && !est.cargando && (
        <FranjaEstado
          ventasHoy={ventasHoy}
          pagados={est.resumen?.cantidadPedidosPagados ?? 0}
          cajaAbierta={cajaAbierta}
          insumosCriticos={est.insumosCriticos}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
        />
      )}

      {/* ========== PEDIDOS EN CURSO ========== */}
      {!est.cargando && <PedidosEnCursoCard pedidos={est.pedidosAbiertos} />}

      {/* ========== ESTADO DEL SALÓN ========== */}
      {est.mesas.length > 0 && (
        <div className="card" data-tour="dsh-mesas">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-semibold">
              <Table2 className="h-5 w-5 text-primary" />
              Estado del salón
              <span className="text-xs font-normal text-slate-500">
                · {mesasLibres} libre{mesasLibres !== 1 ? 's' : ''} · {mesasOcupadas} ocupada
                {mesasOcupadas !== 1 ? 's' : ''}
              </span>
            </h2>
            <Link to="/mesas" className="text-xs font-medium text-blue-600 hover:underline">
              Ver detalle →
            </Link>
          </div>
          <MesasGrid
            mesas={est.mesas}
            pedidoPorMesa={pedidoPorMesa}
            mode="monitor"
            compact
            onMesaLibre={(mesaId) => navigate(`/pos?tipo=comer_aqui&mesa=${mesaId}`)}
            onMesaOcupada={() => navigate('/mesas')}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Sprint 31 — Zona de atención: alertas accionables. Sólo se renderiza si hay
 * algo que requiere acción del dueño; si todo está en orden no ocupa espacio.
 */
function ZonaAtencion({
  cajaAbierta,
  pedidosNoEntregados,
  insumosCriticos,
}: {
  cajaAbierta: boolean;
  pedidosNoEntregados: number;
  insumosCriticos: number;
}) {
  const hayAlgo = !cajaAbierta || pedidosNoEntregados > 0 || insumosCriticos > 0;
  if (!hayAlgo) return null;
  return (
    <div className="space-y-2">
      {!cajaAbierta && (
        <AlertaRow
          tono="amber"
          icon={<Wallet className="h-5 w-5" />}
          titulo="La caja todavía no está abierta"
          detalle="Ábrela para registrar las ventas del día."
          accionLabel="Abrir caja"
          to="/corte-caja"
        />
      )}
      {pedidosNoEntregados > 0 && (
        <AlertaRow
          tono="red"
          icon={<AlertTriangle className="h-5 w-5" />}
          titulo={`${pedidosNoEntregados} pedido${pedidosNoEntregados !== 1 ? 's' : ''} a domicilio sin entregar`}
          detalle="Revísalos en A domicilio: reenviar al cliente o cancelar."
          accionLabel="Ver los pedidos a domicilio"
          to="/domicilios"
        />
      )}
      {/* La card de stock crítico ya es accionable (cargar rápido inline). */}
      {insumosCriticos > 0 && <StockCriticoCard ocultarSiVacio />}
    </div>
  );
}

function AlertaRow({
  tono,
  icon,
  titulo,
  detalle,
  accionLabel,
  to,
}: {
  tono: 'amber' | 'red';
  icon: React.ReactNode;
  titulo: string;
  detalle: string;
  accionLabel: string;
  to: string;
}) {
  const cardCls =
    tono === 'red'
      ? 'border-red-300 bg-red-50 text-red-900'
      : 'border-amber-300 bg-amber-50 text-amber-900';
  const iconCls =
    tono === 'red' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800';
  return (
    <Link to={to} className={`card flex items-center gap-3 ${cardCls} hover:shadow-md`}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconCls}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{titulo}</div>
        <div className="text-sm opacity-80">{detalle}</div>
      </div>
      <span className="shrink-0 text-sm font-semibold underline">{accionLabel} →</span>
    </Link>
  );
}

/**
 * Sprint 31 — Franja de estado: el número que el dueño busca (ventas de hoy)
 * + chips de "todo bien" para caja e inventario. Reemplaza al "Resumen del
 * día" de 3 cards; el detalle contable (alimentos, envíos) vive en Reportes.
 */
function FranjaEstado({
  ventasHoy,
  pagados,
  cajaAbierta,
  insumosCriticos,
  monedaSimbolo,
  locale,
}: {
  ventasHoy: number;
  pagados: number;
  cajaAbierta: boolean;
  insumosCriticos: number;
  monedaSimbolo: string;
  locale: string;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="card flex flex-wrap items-center justify-between gap-4"
      data-tour="dsh-estado"
    >
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">Ventas de hoy</div>
        <div className="font-mono text-3xl font-bold">
          {formatPrecio(ventasHoy, monedaSimbolo, locale)}
        </div>
        <div className="text-xs text-slate-500">
          {pagados} pedido{pagados !== 1 ? 's' : ''} pagado{pagados !== 1 ? 's' : ''}
        </div>
      </div>
      {/* Chips de "todo bien": sólo se muestran cuando el estado es positivo;
          si hay problema, la zona de atención de arriba ya lo señala. */}
      <div className="flex flex-wrap gap-2">
        {cajaAbierta && (
          <button
            type="button"
            onClick={() => navigate('/corte-caja')}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 transition hover:bg-emerald-100"
          >
            <Wallet className="h-4 w-4" />
            Caja abierta
            <CheckCircle2 className="h-4 w-4" />
          </button>
        )}
        {insumosCriticos === 0 && (
          <button
            type="button"
            onClick={() => navigate('/inventario')}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 transition hover:bg-emerald-100"
          >
            <Package className="h-4 w-4" />
            Existencias en orden
            <CheckCircle2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Sprint 30 — Seguimiento del flujo de pedidos. El dueño veía las mesas pero
 * perdía de vista los delivery (listos esperando repartidor, en ruta esperando
 * confirmación) y el avance de los pedidos para comer aquí.
 */
function PedidosEnCursoCard({ pedidos }: { pedidos: PedidoVista[] }) {
  const navigate = useNavigate();
  const cuenta = (tipo: 'comer_aqui' | 'delivery', estados: string[]) =>
    pedidos.filter((p) => p.tipo === tipo && estados.includes(p.estado)).length;

  const comerTotal = pedidos.filter((p) => p.tipo === 'comer_aqui').length;
  const domTotal = pedidos.filter((p) => p.tipo === 'delivery').length;

  return (
    <div className="card" data-tour="dsh-pedidos">
      <h2 className="mb-3 flex items-center gap-2 font-semibold">
        <Receipt className="h-5 w-5 text-primary" />
        Pedidos en curso
      </h2>
      {/* Sprint 30 — dos columnas asimétricas (2/5 + 3/5): "comer aquí" lleva
          menos chips, "a domicilio" lleva más y necesita más ancho. */}
      <div className="grid gap-4 sm:grid-cols-5">
        <div className="md:col-span-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            🍽️ Comer aquí
          </div>
          {comerTotal === 0 ? (
            <p className="text-sm text-slate-400">Sin pedidos para comer aquí.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <ChipEstado
                label="En cocina"
                n={cuenta('comer_aqui', ['pendiente_cocina', 'cocina'])}
                tono="orange"
                onClick={() => navigate('/cocina')}
              />
              <ChipEstado
                label="Listos para llevar a mesa"
                n={cuenta('comer_aqui', ['listo'])}
                tono="green"
                onClick={() => navigate('/cocina')}
              />
              <ChipEstado
                label="Entregados sin cobrar"
                n={cuenta('comer_aqui', ['entregado'])}
                tono="blue"
                onClick={() => navigate('/pedidos')}
              />
            </div>
          )}
        </div>
        <div className="md:col-span-3 md:border-l md:border-slate-200 md:pl-4">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            🏍️ A domicilio
          </div>
          {domTotal === 0 ? (
            <p className="text-sm text-slate-400">Sin pedidos a domicilio.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <ChipEstado
                label="En cocina"
                n={cuenta('delivery', ['pendiente_cocina', 'cocina'])}
                tono="orange"
                onClick={() => navigate('/cocina')}
              />
              <ChipEstado
                label="Listos — esperan repartidor"
                n={cuenta('delivery', ['listo'])}
                tono="green"
                onClick={() => navigate('/domicilios')}
              />
              <ChipEstado
                label="En ruta — esperan confirmación"
                n={cuenta('delivery', ['en_ruta'])}
                tono="blue"
                onClick={() => navigate('/domicilios')}
              />
              <ChipEstado
                label="No entregados"
                n={cuenta('delivery', ['no_entregado'])}
                tono="red"
                onClick={() => navigate('/domicilios')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChipEstado({
  label,
  n,
  tono,
  onClick,
}: {
  label: string;
  n: number;
  tono: 'orange' | 'green' | 'blue' | 'red';
  onClick: () => void;
}) {
  const activo = n > 0;
  const toneCls: Record<string, string> = {
    orange: 'border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100',
    green: 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
    blue: 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100',
    red: 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
        activo ? toneCls[tono] : 'border-slate-200 bg-slate-50 text-slate-400 hover:bg-slate-100'
      }`}
    >
      <span className="font-medium">{label}</span>
      <span
        className={`min-w-[1.5rem] rounded-full px-1.5 py-0.5 text-center text-xs font-bold ${
          activo ? 'bg-white/80' : 'bg-slate-200 text-slate-500'
        }`}
      >
        {n}
      </span>
    </button>
  );
}

/**
 * Sprint 25 — Card proactiva en Dashboard que invita a precargar el día
 * cuando hay sugerencias basadas en lo vendido ayer. Se auto-oculta si no hay
 * nada que sugerir; la dueña puede descartarla para la sesión actual.
 */
function PrecargaSugeridaCard() {
  const navigate = useNavigate();
  const [oculta, setOculta] = useState<boolean>(() => {
    try {
      const hoy = toLocalYYYYMMDD(new Date());
      return sessionStorage.getItem('precarga-card-oculta') === hoy;
    } catch {
      return false;
    }
  });
  const [cantidadSugerencias, setCantidadSugerencias] = useState<number | null>(null);
  const [fechaReferencia, setFechaReferencia] = useState<string>('');

  useEffect(() => {
    if (oculta) return;
    let alive = true;
    (async () => {
      const res = await window.api.insumos.sugerenciaCargaDelDia();
      if (!alive) return;
      if (res.ok) {
        const conSug = res.data.insumos.filter((i) => i.sugerencia > 0).length;
        setCantidadSugerencias(conSug);
        setFechaReferencia(res.data.fechaReferencia);
      } else {
        setCantidadSugerencias(0);
      }
    })();
    return () => {
      alive = false;
    };
  }, [oculta]);

  const dismiss = () => {
    setOculta(true);
    try {
      const hoy = toLocalYYYYMMDD(new Date());
      sessionStorage.setItem('precarga-card-oculta', hoy);
    } catch {
      // ignorar
    }
  };

  if (oculta) return null;
  if (cantidadSugerencias === null) return null; // cargando
  if (cantidadSugerencias === 0) return null;    // nada que sugerir

  const fechaLegible = fechaReferencia
    ? new Date(`${fechaReferencia}T12:00:00`).toLocaleDateString('es-MX', {
        weekday: 'long',
        day: '2-digit',
        month: 'short',
      })
    : 'ayer';

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-4 flex items-start gap-3 shadow-sm" data-tour="dsh-precarga">
      <div className="rounded-full bg-amber-200 p-2 shrink-0">
        <Sparkles className="h-5 w-5 text-amber-800" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-amber-900">
          💡 ¿Precargar el inventario de hoy?
        </div>
        <p className="text-sm text-amber-800 mt-0.5">
          Tienes <strong>{cantidadSugerencias}</strong> insumo{cantidadSugerencias !== 1 ? 's' : ''} con
          sugerencia de carga basada en lo vendido el {fechaLegible}. Te ahorra tiempo de capturar
          a mano cada mañana.
        </p>
        <div className="mt-2 flex gap-2">
          <button
            onClick={() => navigate('/precargar-inventario')}
            className="btn-primary text-sm"
          >
            <Sparkles className="h-4 w-4" />
            Ver sugerencias
          </button>
          <button onClick={dismiss} className="btn-ghost text-sm text-amber-700">
            Ahora no
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="text-amber-600 hover:bg-amber-200/50 p-1 rounded shrink-0"
        aria-label="Cerrar aviso"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
